import { useRef, useState } from 'react';
import { Check, Languages } from 'lucide-react';
import { browserLanguage, useLanguage } from '../../../contexts/LanguageContext';
import { PopMenu } from '../PopMenu';

// THE PUBLISHED TUTORIAL'S LANGUAGE: one round button, and the languages in a menu under it.
//
// It was the app's FR / EN toggle, the sidebar's, pinned over the tutorial: two flags and two
// codes taking a corner of a page whose reader wants the picture. A round icon says "language"
// in any language, and the menu has room for the ones still to come.
//
// THE LANGUAGES NOT YET OFFERED ARE LISTED, GREYED: the page will speak them, and a reader
// looking for theirs sees it is on the way rather than missing. Each in its own name, the way a
// reader who does not read the current one finds theirs.
const LANGS = [
  { code: 'fr', flag: '🇫🇷', name: 'Français', ready: true },
  { code: 'en', flag: '🇬🇧', name: 'English', ready: true },
  { code: 'es', flag: '🇪🇸', name: 'Español', ready: false },
  { code: 'pt', flag: '🇵🇹', name: 'Português', ready: false },
] as const;

export function LanguageMenu() {
  const { language, setLanguage, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const detected = browserLanguage();
  return (
    <>
      <button
        ref={button}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('notice.public.language')}
        aria-expanded={open}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white text-notice-blue shadow-sm transition-colors hover:border-notice-blue/50"
      >
        <Languages className="h-4 w-4" />
      </button>
      {open && (
        <PopMenu anchor={button} onClose={() => setOpen(false)}>
          {LANGS.map(({ code, flag, name, ready }) => (
            <button
              key={code}
              type="button"
              disabled={!ready}
              onClick={() => {
                if (code === 'fr' || code === 'en') setLanguage(code);
                setOpen(false);
              }}
              className="flex w-full min-w-[12rem] items-center gap-3 px-4 py-2.5 text-left font-satoshi text-sm text-slate-800 transition-colors hover:bg-slate-50 disabled:cursor-default disabled:text-slate-300 disabled:hover:bg-transparent"
            >
              <span aria-hidden className={ready ? '' : 'opacity-40'}>{flag}</span>
              <span className="flex-1">
                {name}
                {code === detected && <span className="ml-1.5">{t('notice.public.detected')}</span>}
              </span>
              {language === code && <Check className="h-4 w-4 text-notice-blue" />}
            </button>
          ))}
        </PopMenu>
      )}
    </>
  );
}
